import { useEffect, useRef } from 'react';

const chapters = [
  {
    number: '01',
    title: 'Ganga River Analysis',
    subtitle: 'Core pollution trends & ML predictions',
    desc: 'Comprehensive analysis of the Ganga river using real-world datasets from 2000 onwards. Includes pollution trends, hotspot identification, and machine learning-based forecasting of water quality and risk levels up to 2030.',
    img: 'https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/2da9e15ea81ccdc97f52a5c70c29bb0e.jpeg',
    insights: [
      'Pollution trend analysis (2000–2024)',
      'High-risk hotspot identification',
      'ML-based prediction till 2030',
      'BOD, COD & DO parameter analysis',
    ],
  },
  {
    number: '02',
    title: 'Ganga Basin',
    subtitle: 'River system overview',
    desc: 'Geographical and ecological analysis of the Ganga basin, including tributary networks, regional distribution, and environmental significance influencing pollution patterns.',
    img: 'https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/b86bc98773fec25dd5e069e31eedb540.jpeg',
    insights: [
      'Basin geography & tributary mapping',
      'Regional ecological significance',
      'Pollution pattern distribution',
      'Hydrological flow analysis',
    ],
  },
  {
    number: '03',
    title: 'Drains & Treatment Systems',
    subtitle: 'Pollution flow & infrastructure analysis',
    desc: 'Evaluation of drainage networks, sewage treatment plants (STPs), and inefficiencies in treatment systems contributing to untreated discharge into the river.',
    img: 'https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/ed7e0b4503a7d640fd2bb5f3d832781f.jpeg',
    insights: [
      'STP capacity vs. actual treatment',
      'Untreated sewage discharge volumes',
      'Drain network mapping',
      'Infrastructure efficiency gaps',
    ],
  },
  {
    number: '04',
    title: 'Other Rivers & Tributaries',
    subtitle: 'Impact of connected water systems',
    desc: 'Analysis of tributaries and connected rivers, examining their role in amplifying or reducing pollution levels in the main Ganga river.',
    img: 'https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/4c41e5d4210a12332ec994b664c48d1c.jpeg',
    insights: [
      'Tributary pollution contribution',
      'Cross-river contamination flow',
      'Seasonal variation analysis',
      'Comparative water quality data',
    ],
  },
  {
    number: '05',
    title: 'Industrial Pollution',
    subtitle: 'Industrial discharge & impact analysis',
    desc: 'Study of industrial pollution sources, effluent discharge patterns, and their impact on river health using data-driven insights.',
    img: 'https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/7e652b7c7becc75792e4633993b585c3.jpeg',
    insights: [
      'Industry-wise effluent profiling',
      'Heavy metal contamination mapping',
      'Regulatory compliance analysis',
      'Impact on aquatic biodiversity',
    ],
  },
  {
    number: '06',
    title: 'Final Report',
    subtitle: 'Integrated findings & recommendations',
    desc: 'Complete synthesis of findings, combining all analyses with machine learning predictions and actionable recommendations for river restoration and policy improvement.',
    img: 'https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/42caff14fd39fedce79e9b611dc52aeb.jpeg',
    insights: [
      'Integrated multi-source findings',
      'ML-driven future projections',
      'Policy & restoration recommendations',
      'Actionable intervention roadmap',
    ],
  },
];

export default function ThesisPreview() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.card-animate').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
                (el as HTMLElement).classList.remove('opacity-0', 'translate-y-8');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="thesis" className="py-24 md:py-32 bg-[#F8F5F0]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="text-center mb-10 card-animate opacity-0 translate-y-8 transition-all duration-700">
          <p className="text-[#2A9D8F] text-sm uppercase tracking-[0.25em] font-semibold mb-3">Research</p>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Thesis Sections
            <span className="block text-[#2A9D8F] text-2xl md:text-3xl mt-1">Data-Driven Research &amp; Predictive Analysis (2000–2030)</span>
          </h2>
        </div>

        {/* Chapter Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {chapters.map((ch) => (
            <div
              key={ch.number}
              className="card-animate opacity-0 translate-y-8 transition-all duration-700 group rounded-2xl overflow-hidden border border-[#EDE0D4] bg-[#F8F5F0] hover:-translate-y-1 cursor-default"
            >
              <div className="relative overflow-hidden">
                <img
                  src={ch.img}
                  alt={ch.title}
                  className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <h3 className="text-[#1C1C1C] font-bold text-base mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {ch.title}
                </h3>
                <p className="text-[#2A9D8F] text-xs font-medium mb-2">{ch.subtitle}</p>
                <p className="text-[#6C757D] text-sm leading-relaxed mb-4">{ch.desc}</p>

                {/* Key Insights */}
                <div className="border-t border-[#EDE0D4] pt-3">
                  <p className="text-[#1C1C1C] text-xs font-semibold uppercase tracking-wider mb-2">Key Insights</p>
                  <ul className="space-y-1">
                    {ch.insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[#6C757D] text-xs leading-relaxed">
                        <span className="text-[#2A9D8F] mt-0.5 flex-shrink-0">•</span>
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Thesis Preview – Public Access Coming Soon */}
        <div className="card-animate opacity-0 translate-y-8 transition-all duration-700 mt-16 text-center">
          <div className="inline-flex items-center gap-2 bg-[#EDE0D4] border border-[#6B4F3A]/20 text-[#6B4F3A] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2A9D8F] animate-pulse inline-block"></span>
            Public Access
          </div>
          <h3
            className="text-2xl md:text-3xl font-bold text-[#1C1C1C] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Thesis Preview – Public Access Coming Soon
          </h3>
          <p className="text-[#6C757D] text-sm leading-relaxed max-w-2xl mx-auto mb-7">
            A structured preview of the complete thesis. Full access to detailed research, datasets, and machine learning models will be available after final publication.
          </p>
          <button
            className="whitespace-nowrap inline-flex items-center gap-2 border border-[#2A9D8F]/30 text-[#2A9D8F] bg-[#EDE0D4] hover:bg-[#EDE0D4]/80 font-semibold text-sm px-7 py-2.5 rounded-full transition-colors duration-200 cursor-default"
            title="Full access will be available upon publication."
          >
            <i className="ri-notification-3-line text-base"></i>
            Stay Tuned
          </button>
        </div>

        {/* Research Paper Section */}
        <div className="card-animate opacity-0 translate-y-8 transition-all duration-700 mt-16 rounded-2xl border border-[#EDE0D4] bg-[#EDE0D4]/50 px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex-1">
            <p className="text-[#2A9D8F] text-xs uppercase tracking-[0.2em] font-semibold mb-2">Publication in Progress</p>
            <h3
              className="text-2xl md:text-3xl font-bold text-[#1C1C1C] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Research Paper
            </h3>
            <p className="text-[#6C757D] text-sm leading-relaxed max-w-xl">
              The complete research paper based on this thesis, including methodology, data analysis, and machine learning models, will be published soon.
            </p>
          </div>
          <div className="flex-shrink-0">
            <button
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#F4A261] text-[#1B4332] font-bold text-sm px-6 py-3 rounded-full cursor-default"
              title="Full research access will be available soon. Stay tuned."
            >
              <i className="ri-time-line text-base"></i>
              Coming Soon
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
